import { useEffect, useRef, useState } from "react";
import { TDataShape } from "@hey-api/client-fetch";
import { Options, UsageQuery } from "@/api";
import { Request } from "@/components/CRUD";

type Endpoint<TData extends TDataShape & { body: UsageQuery }, TOut> = Parameters<typeof Request<TData, TOut>>[0];

/**
 * Fetches one chart. Every chart endpoint takes the same UsageQuery, so the chart
 * refetches whenever the query (org, range, selected users) changes; a response for
 * an older query is dropped.
 */
export function useUsageChart<TData extends TDataShape & { body: UsageQuery }, TOut>(
    endpoint: Endpoint<TData, TOut>,
    query: UsageQuery | undefined,
) {
    const [data, setData] = useState<TOut>();
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const generation = useRef(0);
    const key = JSON.stringify(query ?? null);

    useEffect(() => {
        const mine = ++generation.current;
        const isStale = () => mine !== generation.current;
        Request<TData, TOut>(
            endpoint,
            () => ({ body: query }) as unknown as Options<TData, false>,
            (value) => !isStale() && setLoading(value),
            (result) => {
                setData(result);
                setFailed(false);
            },
            () => setFailed(true),
            () => query !== undefined,
            isStale,
        )();
    }, [key]);

    return { data, loading, failed };
}
